import * as THREE from 'three';
import { AttachmentType } from '../../models/standard/AttachmentModel';

// Icons
import attributeIcon from '../../assets/icons/attribute.svg';
import imageIcon from '../../assets/icons/image.svg';
import panoramaIcon from '../../assets/icons/panorama.svg';
import pdfIcon from '../../assets/icons/pdf.svg';

const ICON_MAP: Record<AttachmentType, string> = {
  [AttachmentType.ATTRIBUTE]: attributeIcon,
  [AttachmentType.IMAGE]: imageIcon,
  [AttachmentType.PANORAMA]: panoramaIcon,
  [AttachmentType.PDF]: pdfIcon,
};

const textureCache = new Map<AttachmentType, THREE.Texture>();

const createAttachmentSpriteTexture = (src: string): Promise<THREE.Texture> => {
  const scaleFactor = 5; 
  const defaultSize = 24; 
  const image = new Image();
  image.crossOrigin = 'anonymous';
  
  return new Promise<THREE.Texture>((resolve, reject) => {
    image.onload = () => {
      // Firefox Fix: SVGs might report width=0. Fallback to defaultSize.
      const baseWidth = image.width > 0 ? image.width : defaultSize;
      const baseHeight = image.height > 0 ? image.height : defaultSize;
      
      const canvas = document.createElement('canvas');
      canvas.width = baseWidth * scaleFactor;
      canvas.height = baseHeight * scaleFactor;
      
      const ctx = canvas.getContext('2d');
      if (ctx) {
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        
        // 1. Draw the image (icon) first
        ctx.drawImage(image, 0, 0, canvas.width, canvas.height);
        
        // 2. Change Composite Mode to "Source In"
        // This means: "Only keep the new pixels where they overlap with existing opaque pixels"
        ctx.globalCompositeOperation = 'source-in';
        
        // 3. Draw a solid white rectangle over the whole canvas
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        
        // (Optional) Reset composite operation if you plan to draw more
        ctx.globalCompositeOperation = 'source-over';

        const texture = new THREE.CanvasTexture(canvas);
        
        // --- FIREFOX / WEBGL FIXES ---
        texture.flipY = true;
        texture.colorSpace = THREE.SRGBColorSpace;
        
        // Fix for Non-Power-Of-Two (NPOT) textures
        texture.minFilter = THREE.LinearFilter; 
        texture.magFilter = THREE.LinearFilter;
        texture.generateMipmaps = false; 

        texture.needsUpdate = true;
        
        resolve(texture);
      } else {
        reject(new Error('Failed to get canvas context'));
      }
    };
    
    image.onerror = (e) => {
      reject(new Error(`Error loading texture from ${src}: ${e}`));
    };
    
    image.src = src;  
  });
};

export const getAttachmentSpriteTexture = async (type: AttachmentType) => {
  if (textureCache.has(type)) {
    return textureCache.get(type)!;
  }

  const iconSrc = ICON_MAP[type] || attributeIcon; 

  try {
    const texture = await createAttachmentSpriteTexture(iconSrc);
    textureCache.set(type, texture);
    return texture;
  } catch (error) {
    console.error(error);
    throw error;
  }
};

export const disposeAttachmentSpriteTextures = () => {
  textureCache.forEach((texture) => {
    texture.dispose();
  });
  textureCache.clear();
};